/**
 * 검색엔진 쪽 — 엣지 크롤 도달과 인용 (260928-oai-searchbot-is-the-index-gate).
 *
 * 생성 엔진 인용의 **선행 조건은 `OAI-SearchBot` 도달**이다. 이 화면은 사이트 9곳을 나란히 놓아
 * "어디가 색인에 들어갔나"를 한눈에 보이게 한다.
 */
import { seo } from '@geo-admin/shared/contract/api/index.ts';
import type { RouteResponse } from '@geo-admin/shared/contract/route.ts';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { CrawlTrend } from '../components/CrawlTrend.tsx';
import { DataTable } from '../components/data-table.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { RateCell } from '../components/RateCell.tsx';
import { Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { Select } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';

type ZoneRow = RouteResponse<typeof seo.crawlSummary>['zones'][number];

const num = (v: number) => v.toLocaleString();

/**
 * 표의 열이 무엇인지. ★ 봇 이름만으로는 **학습용과 색인용이 구별되지 않는다** —
 * GPTBot 이 수천 번 와도 색인에는 안 들어간다(FACTS F-16). 그 차이를 여기서 말한다.
 */
const LEGEND: { term: string; tone: 'key' | 'plain'; desc: string }[] = [
  {
    term: 'AI Search',
    tone: 'key',
    desc: 'Cloudflare 가 IP 로 검증한 AI 검색 크롤러. ChatGPT 색인을 채우는 것이 여기 든다 — 이 화면의 기준값',
  },
  {
    term: 'OAI-SearchBot',
    tone: 'plain',
    desc: 'OpenAI 의 검색 색인 크롤러. UA 문자열 기준이라 검증 전이고, 하루 상위 300개만 저장돼 적게 잡힐 수 있다',
  },
  {
    term: 'GPTBot',
    tone: 'plain',
    desc: 'OpenAI 의 학습용 크롤러. 색인과 무관하다 — 많이 와도 인용으로 이어지지 않는다',
  },
  { term: 'Googlebot', tone: 'plain', desc: '구글 검색 색인 크롤러. 이미지 전용(Googlebot-Image)은 빼고 센다' },
  { term: '페이지 요청', tone: 'plain', desc: '봇을 가리지 않은 전체. apex·www 만이고 img.* 는 이미지로 따로 센다' },
  {
    term: '수집',
    tone: 'plain',
    desc: 'Cloudflare 보존이 5~6일이라 기간보다 작은 것이 정상이다. 빠진 날은 0이 아니라 모르는 날이다',
  },
];

export function SeoPage() {
  const [days, setDays] = useState(7);
  const [zone, setZone] = useState<string | null>(null);
  const summary = useApi(seo.crawlSummary, { query: { days } });
  const trend = useApi(seo.crawlTrend, { query: { days, zone: zone ?? '' } }, { enabled: zone !== null });

  const columns: ColumnDef<ZoneRow, unknown>[] = [
    {
      header: '사이트',
      accessorKey: 'zoneHost',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.zoneHost}</div>
          {row.original.siteName === null && <div className="text-xs text-slate-400">미등록 — 인용을 셀 수 없다</div>}
        </div>
      ),
    },
    {
      header: '수집',
      accessorKey: 'daysCollected',
      cell: ({ row }) => (
        <div className="tabular-nums">
          <div>
            {row.original.daysCollected}/{days}일
          </div>
          <div className="text-xs text-slate-500">{row.original.lastDate ?? '—'}</div>
        </div>
      ),
    },
    {
      header: 'AI Search',
      accessorKey: 'aiSearch',
      cell: ({ row }) => <span className="font-semibold tabular-nums">{num(row.original.aiSearch)}</span>,
    },
    { header: 'OAI-SearchBot', accessorKey: 'oaiSearchBot', cell: ({ row }) => num(row.original.oaiSearchBot) },
    { header: 'GPTBot', accessorKey: 'gptbot', cell: ({ row }) => num(row.original.gptbot) },
    { header: 'Googlebot', accessorKey: 'googlebot', cell: ({ row }) => num(row.original.googlebot) },
    { header: '페이지 요청', accessorKey: 'page', cell: ({ row }) => num(row.original.page) },
    {
      header: `인용률 (${days}일)`,
      accessorKey: 'cited',
      cell: ({ row }) => <RateCell rate={row.original.cited} />,
    },
  ];

  const selected = summary.data?.zones.find((z) => z.zoneTag === zone) ?? null;

  return (
    <>
      <PageTitle
        title="검색엔진 도달"
        description="생성 엔진 인용의 선행 조건은 OAI-SearchBot 도달이다. 행을 누르면 일별 추이를 본다."
        actions={
          <Select className="w-28" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 14, 30, 90].map((d) => (
              <option key={d} value={d}>
                {d}일
              </option>
            ))}
          </Select>
        }
      />
      <Card>
        <CardHeader title="zone별 크롤 도달" description="열 이름이 뜻하는 것은 아래에 적었다." />
        <CardBody className="border-b border-slate-100 bg-slate-50/60">
          <dl className="grid gap-x-8 gap-y-2 text-xs sm:grid-cols-2 xl:grid-cols-3">
            {LEGEND.map((l) => (
              <div key={l.term} className="flex gap-2">
                <dt
                  className={
                    l.tone === 'key'
                      ? 'w-28 shrink-0 font-semibold text-slate-900'
                      : 'w-28 shrink-0 font-medium text-slate-600'
                  }
                >
                  {l.term}
                </dt>
                <dd className="text-slate-500">{l.desc}</dd>
              </div>
            ))}
          </dl>
        </CardBody>
        {summary.isLoading ? (
          <Loading />
        ) : summary.error ? (
          <ErrorBox error={summary.error} />
        ) : (
          <DataTable
            columns={columns}
            data={summary.data?.zones ?? []}
            empty="아직 수집된 크롤 도달이 없습니다."
            onRowClick={(r) => setZone(r.zoneTag === zone ? null : r.zoneTag)}
          />
        )}
      </Card>

      {zone !== null && (
        <Card>
          <CardHeader
            title={`${selected?.zoneHost ?? ''} — 일별 추이`}
            description={
              '끊긴 구간은 0이 아니라 수집 안 된 날이다. ' +
              '인용은 그 전 크롤의 결과라 시차가 있다 — 같은 날끼리 맞춰 읽지 않는다. ' +
              '인용은 비율이 아니라 건수다(분모가 고객·질문이 늘면 커진다).'
            }
          />
          <CardBody>
            {trend.isLoading ? (
              <Loading />
            ) : trend.error ? (
              <ErrorBox error={trend.error} />
            ) : (
              <CrawlTrend daily={trend.data?.daily ?? []} />
            )}
          </CardBody>
        </Card>
      )}
    </>
  );
}
